/**
 * MelCharts — helpers de gráfico (recharts) com a identidade AQUA/glass.
 *
 * Pensados para o dashboard de ai-metrics, mas reutilizáveis em qualquer
 * resource. Cada componente é "burro": recebe `data` já agregada e os nomes
 * dos campos; quem agrega são as telas (ex.: ai-metrics/list.tsx soma
 * cost_usd por dia/provider a partir dos fixtures).
 *
 * Paleta vinda de src/theme/tokens.ts para não dar drift de marca.
 */
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Empty } from "antd";
import { melColors } from "@/theme/tokens";

/** Paleta categórica do MEL (séries de gráfico). */
export const CHART_COLORS: string[] = [
  melColors.aqua500,
  melColors.turquoise500,
  melColors.info,
  "#8B5CF6",
  "#F59E0B",
  "#EC4899",
  melColors.aqua700,
];

const axisStyle = { fontSize: 12, fill: melColors.inkSoft } as const;

const tooltipStyle = {
  background: "rgba(255,255,255,0.92)",
  border: `1px solid ${melColors.glassBorder}`,
  borderRadius: 12,
  boxShadow: "0 8px 32px rgba(6,182,212,0.18)",
  fontSize: 12,
  color: melColors.ink,
} as const;

type Row = Record<string, number | string>;

/**
 * Adapta `valueFormatter?: (v:number)=>string` para os props `formatter`/
 * `tickFormatter` do recharts (cujas assinaturas aceitam `unknown`). Evita
 * conflito de tipos sem perder a formatação pt-BR/USD.
 */
type RechartsFormatter = (value: unknown, ...rest: unknown[]) => string;
const asFormatter = (fn?: (v: number) => string): RechartsFormatter | undefined =>
  fn ? ((v: unknown) => fn(Number(v))) : undefined;

interface SeriesChartProps {
  data: Row[];
  /** chave do eixo X (categoria), ex.: "date". */
  xKey: string;
  /** uma ou mais séries Y a desenhar. */
  series: { key: string; name: string; color?: string }[];
  height?: number;
  /** formatação do valor no eixo Y / tooltip. */
  valueFormatter?: (v: number) => string;
}

const emptyBox = (height: number) => (
  <div style={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sem dados no período" />
  </div>
);

/** Gráfico de área empilhável — ideal para custo de IA por dia. */
export const MelAreaChart: React.FC<SeriesChartProps> = ({
  data,
  xKey,
  series,
  height = 260,
  valueFormatter,
}) => {
  if (!data.length) return emptyBox(height);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <defs>
          {series.map((s, i) => {
            const color = s.color ?? CHART_COLORS[i % CHART_COLORS.length];
            return (
              <linearGradient id={`mel-grad-${s.key}`} key={s.key} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.5} />
                <stop offset="95%" stopColor={color} stopOpacity={0.04} />
              </linearGradient>
            );
          })}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(45,212,191,0.18)" vertical={false} />
        <XAxis dataKey={xKey} tick={axisStyle} tickLine={false} axisLine={false} />
        <YAxis
          tick={axisStyle}
          tickLine={false}
          axisLine={false}
          width={56}
          tickFormatter={asFormatter(valueFormatter)}
        />
        <Tooltip
          contentStyle={tooltipStyle}
          formatter={asFormatter(valueFormatter) as never}
        />
        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {series.map((s, i) => {
          const color = s.color ?? CHART_COLORS[i % CHART_COLORS.length];
          return (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stackId="1"
              stroke={color}
              strokeWidth={2}
              fill={`url(#mel-grad-${s.key})`}
            />
          );
        })}
      </AreaChart>
    </ResponsiveContainer>
  );
};

/** Linha simples (uma ou mais séries). */
export const MelLineChart: React.FC<SeriesChartProps> = ({
  data,
  xKey,
  series,
  height = 260,
  valueFormatter,
}) => {
  if (!data.length) return emptyBox(height);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(45,212,191,0.18)" vertical={false} />
        <XAxis dataKey={xKey} tick={axisStyle} tickLine={false} axisLine={false} />
        <YAxis tick={axisStyle} tickLine={false} axisLine={false} width={56} tickFormatter={asFormatter(valueFormatter)} />
        <Tooltip contentStyle={tooltipStyle} formatter={asFormatter(valueFormatter) as never} />
        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {series.map((s, i) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.color ?? CHART_COLORS[i % CHART_COLORS.length]}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

/** Barras (ex.: requests por provider, confusões de categoria). */
export const MelBarChart: React.FC<SeriesChartProps & { horizontal?: boolean }> = ({
  data,
  xKey,
  series,
  height = 260,
  valueFormatter,
  horizontal,
}) => {
  if (!data.length) return emptyBox(height);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout={horizontal ? "vertical" : "horizontal"}
        margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(45,212,191,0.18)" vertical={false} />
        {horizontal ? (
          <>
            <XAxis type="number" tick={axisStyle} tickLine={false} axisLine={false} tickFormatter={asFormatter(valueFormatter)} />
            <YAxis type="category" dataKey={xKey} tick={axisStyle} tickLine={false} axisLine={false} width={90} />
          </>
        ) : (
          <>
            <XAxis dataKey={xKey} tick={axisStyle} tickLine={false} axisLine={false} />
            <YAxis tick={axisStyle} tickLine={false} axisLine={false} width={56} tickFormatter={asFormatter(valueFormatter)} />
          </>
        )}
        <Tooltip
          cursor={{ fill: "rgba(6,182,212,0.06)" }}
          contentStyle={tooltipStyle}
          formatter={asFormatter(valueFormatter) as never}
        />
        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {series.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.name}
            fill={s.color ?? CHART_COLORS[i % CHART_COLORS.length]}
            radius={horizontal ? [0, 6, 6, 0] : [6, 6, 0, 0]}
            maxBarSize={48}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

interface DonutProps {
  data: { name: string; value: number }[];
  height?: number;
  valueFormatter?: (v: number) => string;
}

/** Rosca (ex.: custo por provider). */
export const MelDonutChart: React.FC<DonutProps> = ({ data, height = 260, valueFormatter }) => {
  if (!data.length || data.every((d) => d.value === 0)) return emptyBox(height);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius="55%"
          outerRadius="80%"
          paddingAngle={2}
          stroke="rgba(255,255,255,0.6)"
        >
          {data.map((_, i) => (
            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} formatter={asFormatter(valueFormatter) as never} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
};
